import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { ArrowLeft, Mail, CheckCircle, AlertCircle, AlertTriangle, Shield, WifiOff } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import './ForgotPassword.css';
import { useBranding, useBrandLogo, useBrandName } from '@/contexts/BrandingContext';
import axios from 'axios';
import baseURL from '../../../http';
import { accountHref } from '@/features/site/accountLinks';

// ---------- types ----------
interface ForgotPasswordData {
  email: string;
}

interface ValidationErrors {
  email?: string;
  general?: string;
}

// ---------- constants ----------
const RESEND_COOLDOWN_MS = 60_000; // 60 seconds cooldown
const MAX_RESET_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const SESSION_STORAGE_KEY = 'forgotPasswordFormData';
const ATTEMPTS_STORAGE_KEY = 'resetAttempts';

// ---------- component ----------
const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Enhanced state management
  // Whose screen this is. Falls back to the platform defaults, so this
  // renders exactly as before on our own domain.
  const brandName = useBrandName();
  const { branding, isPlatform: isPlatformBrand } = useBranding();
  const brandTagline = branding.tagline || '';
  const { logoUrl: brandLogo, fallbackUrl: platformLogo } = useBrandLogo();
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});
  const [forgotPasswordData, setForgotPasswordData] = useState<ForgotPasswordData>({
    email: '',
  });
  const [cooldownLeftMs, setCooldownLeftMs] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [lockoutTimeLeft, setLockoutTimeLeft] = useState(0);

  // Refs for cleanup
  const lastRequestAtRef = useRef(0);
  const cooldownTimerRef = useRef<number | null>(null);
  const lockoutTimerRef = useRef<number | null>(null);

  // ---- Authentication check on page load ----
  useEffect(() => {
    const checkExistingAuth = () => {
      try {
        // Check for valid authentication tokens
        const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const tokenExpiration = localStorage.getItem('token_expiration') || sessionStorage.getItem('token_expiration');

        // If we have tokens, check if they're still valid
        if (authToken || accessToken) {
          // Check token expiration if available
          if (tokenExpiration) {
            const expirationTime = parseInt(tokenExpiration);
            const currentTime = Date.now();

            if (currentTime < expirationTime) {
              // Token is still valid, redirect to admin dashboard
              navigate('/admin_dashboard_page');
              return;
            } else {
              // Token expired, clear it
              localStorage.removeItem('authToken');
              localStorage.removeItem('access_token');
              localStorage.removeItem('token_expiration');
              sessionStorage.removeItem('authToken');
              sessionStorage.removeItem('access_token');
              sessionStorage.removeItem('token_expiration');
            }
          } else {
            // No expiration info, assume token is valid and redirect
            navigate('/admin_dashboard_page');
            return;
          }
        }

        // Also check for other auth indicators
        const isAuthenticated = localStorage.getItem('isAuthenticated');
        const user = localStorage.getItem('user');

        if (isAuthenticated === 'true' && user) {
          try {
            const userData = JSON.parse(user);
            if (userData && userData.id) {
              // User data exists, redirect to admin dashboard page
              navigate('/admin_dashboard_page');
              return;
            }
          } catch (error) {
            console.warn('Failed to parse user data:', error);
            // Clear invalid user data
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
          }
        }
      } catch (error) {
        console.warn('Failed to check existing authentication:', error);
      }
    };

    // Check authentication on component mount
    checkExistingAuth();
  }, [navigate]);

  // ---- Online/Offline detection ----
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ---- Form persistence ----
  useEffect(() => {
    // Load saved form data
    try {
      const saved = localStorage.getItem(SESSION_STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setForgotPasswordData(prev => ({
          ...prev,
          email: data.email || ''
        }));
      }
    } catch (error) {
      console.warn('Failed to load saved form data:', error);
    }

    // Check for account lockout
    checkAccountLockout();

    // Set document title
    // document.title = 'Forgot Password - EduExpertAI';
  }, []);

  // Save form data when email changes
  useEffect(() => {
    if (forgotPasswordData.email) {
      try {
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
          email: forgotPasswordData.email
        }));
      } catch (error) {
        console.warn('Failed to save form data:', error);
      }
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, [forgotPasswordData.email]);

  // ---- Account lockout management ----
  const checkAccountLockout = () => {
    try {
      const attempts = localStorage.getItem(ATTEMPTS_STORAGE_KEY);
      if (attempts) {
        const data = JSON.parse(attempts);
        const now = Date.now();

        if (data.count >= MAX_RESET_ATTEMPTS && data.lockoutUntil > now) {
          setIsAccountLocked(true);
          setLockoutTimeLeft(data.lockoutUntil - now);
          startLockoutTimer(data.lockoutUntil);
        } else if (data.lockoutUntil <= now) {
          // Lockout expired, reset attempts
          localStorage.removeItem(ATTEMPTS_STORAGE_KEY);
          setIsAccountLocked(false);
          setLockoutTimeLeft(0);
        }
      }
    } catch (error) {
      console.warn('Failed to check account lockout:', error);
    }
  };

  const recordFailedAttempt = () => {
    try {
      const attempts = localStorage.getItem(ATTEMPTS_STORAGE_KEY);
      const now = Date.now();
      let data = { count: 0, lockoutUntil: 0 };

      if (attempts) {
        data = JSON.parse(attempts);
        if (data.lockoutUntil <= now) {
          data.count = 0;
        }
      }

      data.count += 1;

      if (data.count >= MAX_RESET_ATTEMPTS) {
        data.lockoutUntil = now + LOCKOUT_DURATION;
        setIsAccountLocked(true);
        setLockoutTimeLeft(LOCKOUT_DURATION);
        startLockoutTimer(data.lockoutUntil);

        toast({
          title: 'Account Temporarily Locked',
          description: 'Too many reset attempts. Please try again in 15 minutes.',
          variant: 'destructive',
        });
      }

      localStorage.setItem(ATTEMPTS_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to record failed attempt:', error);
    }
  };

  const clearFailedAttempts = () => {
    try {
      localStorage.removeItem(ATTEMPTS_STORAGE_KEY);
      setIsAccountLocked(false);
      setLockoutTimeLeft(0);
      if (lockoutTimerRef.current) {
        clearInterval(lockoutTimerRef.current);
      }
    } catch (error) {
      console.warn('Failed to clear failed attempts:', error);
    }
  };

  const startLockoutTimer = (lockoutUntil: number) => {
    if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);

    lockoutTimerRef.current = window.setInterval(() => {
      const remaining = lockoutUntil - Date.now();
      setLockoutTimeLeft(remaining);

      if (remaining <= 0) {
        setIsAccountLocked(false);
        setLockoutTimeLeft(0);
        localStorage.removeItem(ATTEMPTS_STORAGE_KEY);
        if (lockoutTimerRef.current) {
          clearInterval(lockoutTimerRef.current);
        }
        toast({
          title: 'Account Unlocked',
          description: 'You can now try requesting password reset again.',
        });
      }
    }, 1000);
  };

  // ---- Enhanced validation ----
  const normalizeEmail = (email: string) => email.trim().toLowerCase();

  const validateEmail = (email: string): string | undefined => {
    if (!email) return 'Email address is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address';
    if (email.length > 254) return 'Email address is too long';
    return undefined;
  };

  const validate = (): boolean => {
    const errors: ValidationErrors = {};
    const email = normalizeEmail(forgotPasswordData.email);

    const emailError = validateEmail(email);
    if (emailError) errors.email = emailError;

    // Check if user is offline
    if (!isOnline) {
      errors.general = 'You appear to be offline. Please check your internet connection.';
    }

    // Check account lockout
    if (isAccountLocked) {
      const minutes = Math.ceil(lockoutTimeLeft / 60000);
      errors.general = `Account temporarily locked. Try again in ${minutes} minutes.`;
    }

    // Check cooldown
    if (cooldownLeftMs > 0) {
      const seconds = Math.ceil(cooldownLeftMs / 1000);
      errors.general = `Please wait ${seconds} seconds before requesting another reset email.`;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ---- Cooldown management ----
  const canResend = () => Date.now() - lastRequestAtRef.current >= RESEND_COOLDOWN_MS;

  const startCooldown = () => {
    lastRequestAtRef.current = Date.now();
    setCooldownLeftMs(RESEND_COOLDOWN_MS);
    if (cooldownTimerRef.current) window.clearInterval(cooldownTimerRef.current);

    cooldownTimerRef.current = window.setInterval(() => {
      const left = Math.max(0, RESEND_COOLDOWN_MS - (Date.now() - lastRequestAtRef.current));
      setCooldownLeftMs(left);
      if (left === 0 && cooldownTimerRef.current) {
        window.clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    }, 250);
  };

  // Format lockout time
  const formatLockoutTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) window.clearInterval(cooldownTimerRef.current);
      if (lockoutTimerRef.current) window.clearInterval(lockoutTimerRef.current);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForgotPasswordData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess(false);

    // Clear field-specific errors when user starts typing
    if (fieldErrors[name as keyof ValidationErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Real-time validation for better UX
    if (name === 'email' && value) {
      const emailError = validateEmail(normalizeEmail(value));
      if (emailError) {
        setFieldErrors(prev => ({ ...prev, email: emailError }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setFieldErrors({});

    // Check if any of the fields are empty
    if (!forgotPasswordData.email) {
      const errorMessage = 'Please fill all the fields';
      setError(errorMessage);
      toast({
        title: 'Warning',
        description: errorMessage,
        variant: 'destructive',
      });
      return;
    }

    // Check if entered email is in a valid format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordData.email)) {
      const errorMessage = 'Please enter a valid email address';
      setError(errorMessage);
      toast({
        title: 'Warning',
        description: errorMessage,
        variant: 'destructive',
      });
      return;
    }

    if (!validate()) return;

    setIsLoading(true);

    try {
      const normalizedEmail = normalizeEmail(forgotPasswordData.email);

      // Our endpoint, not Firebase's client SDK. The backend asks Firebase for
      // the reset link and then delivers it through the notification system, so
      // the mail arrives under the institute's own name rather than the
      // Firebase project's.
      await axios.post(`${baseURL}/public/ForgotPassword/`, {
        email: normalizedEmail,
      });

      clearFailedAttempts();
      startCooldown();

      setSuccess(true);
      // Deliberately does NOT name the address back. Echoing it confirmed to
      // anyone typing addresses in whether each one had an account.
      toast({
        title: 'Check your inbox',
        description:
          'If that address has an account, a password reset link is on its way.',
      });

      navigate(accountHref('/login'));
    } catch (error: any) {
      recordFailedAttempt();

      // The endpoint answers identically whether or not the account exists, so
      // there is nothing account-specific to branch on. Only transport and
      // rate-limit failures reach here.
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;

      let errorMessage: string;
      if (status === 429) {
        errorMessage = 'Too many attempts. Please wait a moment and try again.';
      } else if (status === 400) {
        errorMessage = 'Please enter a valid email address.';
      } else if (status === 503) {
        errorMessage =
          'We could not send the reset link just now. Please try again shortly.';
      } else {
        errorMessage = 'Failed to send the reset email. Please try again later.';
      }

      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper variables for form state
  const isFormDisabled = isLoading || isAccountLocked || !isOnline || cooldownLeftMs > 0;

  return (
    <div className="forgot-password-container">
      {/* Network Status Indicator */}
      {!isOnline && (
        <div className="fixed top-4 right-4 z-50 bg-red-500 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <WifiOff className="w-4 h-4" />
          <span>You're offline</span>
        </div>
      )}

      <div className="forgot-password-content">
        {/* Header */}
        <div className="forgot-password-header">
          <div className="forgot-password-logo-section">
            <div className="forgot-password-logo">
              <div className="forgot-password-logo-icon">
                <img
                  src={brandLogoFailed ? platformLogo : brandLogo}
                  alt={brandName}
                  onError={() => setBrandLogoFailed(true)}
                />
              </div>
              <h1 className="forgot-password-logo-text">{brandName}</h1>
            </div>
          {brandTagline ? (
            <p className="text-sm text-muted-foreground mt-1">{brandTagline}</p>
          ) : null}
          </div>
        </div>

        <Card className="forgot-password-card">
          <CardHeader className="forgot-password-card-header">
            <CardTitle className="forgot-password-card-title">
              {success ? 'Check Your Email' : 'Forgot Password'}
            </CardTitle>
            <CardDescription>
              {success
                ? "We've sent a password reset link to your email address."
                : 'Enter your registered email address to reset your password.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="forgot-password-card-content">
            {success ? (
              <div className="forgot-password-success">
                <div className="forgot-password-success-icon">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <div className="forgot-password-success-content">
                  <h3>Verification Email Sent!</h3>
                  <p>
                    A password reset link has been sent to{' '}
                    <strong>{forgotPasswordData.email}</strong>. Please check your email and click
                    the link to reset your password.
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    If you don't see the email, check your spam folder or try again in a few minutes.
                  </p>
                  {cooldownLeftMs > 0 && (
                    <p className="text-sm text-blue-600 mt-2">
                      Next reset available in {Math.ceil(cooldownLeftMs / 1000)} seconds
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* General error display */}
                {(error || fieldErrors.general) && (
                  <Alert variant="destructive" className="forgot-password-error">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {fieldErrors.general || error}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Account lockout warning */}
                {isAccountLocked && (
                  <Alert variant="destructive" className="forgot-password-error">
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      Account temporarily locked due to multiple reset attempts.
                      <br />
                      Time remaining: <strong>{formatLockoutTime(lockoutTimeLeft)}</strong>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Cooldown warning */}
                {cooldownLeftMs > 0 && !isAccountLocked && (
                  <Alert className="forgot-password-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Please wait {Math.ceil(cooldownLeftMs / 1000)} seconds before requesting another reset email.
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="forgot-password-form">
                  <div className="forgot-password-field">
                    <Label htmlFor="email" className="forgot-password-label">
                      Email Address *
                    </Label>
                    <div className="forgot-password-input-wrapper">
                      <Mail className="forgot-password-input-icon" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your registered email"
                        value={forgotPasswordData.email}
                        onChange={handleInputChange}
                        className="forgot-password-input"
                        required
                        autoFocus
                        autoComplete="email"
                        disabled={isFormDisabled}
                        aria-invalid={!!fieldErrors.email}
                        aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                      />
                    </div>
                    {fieldErrors.email && (
                      <div id="email-error" className="forgot-password-field-error">{fieldErrors.email}</div>
                    )}
                  </div>

                  <Button type="submit" className="forgot-password-submit-btn" disabled={isFormDisabled}>
                    {isLoading ? (
                      <>
                        <div className="forgot-password-spinner" />
                        Sending Email...
                      </>
                    ) : cooldownLeftMs > 0 ? (
                      `Resend in ${Math.ceil(cooldownLeftMs / 1000)}s`
                    ) : (
                      'Send Password Reset Email'
                    )}
                  </Button>
                </form>
              </>
            )}
          </CardContent>

          <CardFooter className="forgot-password-footer">
            <div className="forgot-password-links">
              <Link to={accountHref('/login')} className="forgot-password-back-link">
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
              {/* Self sign-up is the platform's; an institute's site never offers it
                  (its people are added by the institute, or request to join). */}
              {isPlatformBrand && (
                <>
                  <span className="forgot-password-divider">•</span>
                  <Link to="/signup" className="forgot-password-signup-link">
                    Create Account
                  </Link>
                </>
              )}
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
